import moment from "moment";
import { ElMessageBox, ElMessage } from "element-plus";
import { set } from "nuxt/dist/app/compat/capi";
import { Pagination } from "~/types/pagination";
import {
  getRequest,
  putRequest,
  downloadWithSavePicker,
  postRequest,
  deleteRequest,
} from "~/apis/requests/useApiCall";
import { definePiniaStore, exportToExcel } from "#imports";
import { TableDataWithPagination } from "~/types/generics/tableDataWithPagination";
import { useAuthStore } from "~/stores/auth";
import { useConfigStore } from "~/stores/configStore";
import {
  Conversation,
  Ticket,
  TicketPriority,
  TicketSource,
  TicketStatus,
  TicketScheduledMessage,
} from "~/projects/help-desk/types";

import {
  TicketGroup,
  TicketGroupInfo,
} from "~/projects/help-desk/types/ticketGroup";
import {
  BulkActionResponse,
  BulkAssignAgentError,
  BulkAssignResult,
  BulkPickResult,
  BulkResolveResult,
  BulkUpdateResult,
} from "~/projects/help-desk/types/bulkAssign";
import { DEFAULT_PROFILE_CONFLICT } from "~/projects/help-desk/utils/profileConflict";
import { reportBulkOutcome } from "~/projects/help-desk/utils/bulkTicketFeedback";

export const useTicketsStore = definePiniaStore("ticketsStore", {
  state: () => ({
    _loading: true,
    _tableData: [] as Array<Ticket>,
    _filterData: {} as any,
    _pagination: {} as Pagination,
    _showModalForTicketMessagesPreview: false,
    _statuses: [] as Array<TicketStatus>,
    _sources: [] as Array<TicketSource>,
    _priorities: [] as Array<TicketPriority>,
    _ticketGroups: [] as TicketGroup[],
    _ticketMessagesForPreview: [] as Array<Conversation>,
    // Ticket the preview modal is currently showing — kept so the modal can look
    // up the customer's earlier tickets without the opener passing it again.
    _ticketForPreview: {} as Ticket,
    _referrals: [] as any[],
    multipleSelection: [],
    showMergeModal: false,
    showExcScenariosModal: false,
    showLogTimeModal: false,
    showTicketsFiltersModal: false,
    _scheduledMessages: [] as Array<TicketScheduledMessage>,
    _scheduledMessagesLoading: false,
    _scheduledMessagesPagination: {} as Pagination,
    _selectedScheduledMessage: {} as TicketScheduledMessage,
    _scheduledMessageFilters: {} as Record<string, any>,
    _scheduledMessagesPerPage: 50,
    showScheduleCreateModal: false,
    showScheduleEditModal: false,
    // Draft typed in the inbox composer, prefilled into the create modal's
    // first message field when scheduling from the composer.
    scheduleDraftMessage: "",
  }),
  actions: {
    toggleTicketsFiltersModal() {
      this.showTicketsFiltersModal = !this.showTicketsFiltersModal;
    },
    openExcScenarioModal() {
      this.showExcScenariosModal = true;
    },
    openLogTimeModal() {
      this.showLogTimeModal = true;
    },
    closeExcScenarioModal() {
      this.showExcScenariosModal = false;
    },
    closeLogTimeModal() {
      this.showLogTimeModal = false;
    },
    openMergeModal() {
      this.showMergeModal = true;
    },
    closeMergeModal() {
      this.showMergeModal = false;
    },
    selectionChange(arr: any) {
      this.multipleSelection = arr;
      console.log(arr);
    },
    addNewTicketToTableData(ticket: Ticket) {
      if (this._pagination.current_page === 1) {
        this._tableData.unshift(ticket);
      }

      const perPage = this._pagination.per_page
        ? this._pagination.per_page
        : 10;
      if (this._tableData.length > perPage) {
        console.log("i am popping");
        this._tableData.pop();
      }
    },
    removeAssignedTicketFromTableData(ticket: Ticket) {
      const index = this._tableData.findIndex(
        (t: Ticket) => t.id === ticket.id,
      );
      if (index !== -1) {
        this._tableData.splice(index, 1);
      }
    },
    setFilterData(filterData: object) {
      this._filterData = filterData;
    },

    async getTickets(
      pageNo = 1,
      sortBy = "id",
      ascending = false,
      includeAutomatedTickets = false,
      automationOutcome?: number | null,
    ) {
      this._loading = true;
      const sortDir = ascending ? "asc" : "desc";
      let url = `desk/tickets?page=${pageNo}&sort_column=${sortBy}&order=${sortDir}`;
      // Strict boolean check: some callers bind this action directly to @click,
      // which passes the DOM event (truthy) as the first arg — treat only an
      // explicit `true` as "include automated tickets".
      if (includeAutomatedTickets === true) {
        url += `&include_automated_tickets=true`;
      }
      if (automationOutcome) {
        url += `&automation_outcome=${automationOutcome}`;
      }
      for (const [key, value] of Object.entries(this._filterData)) {
        // ticket_analysis and page_no are frontend-only view state (the
        // Live/Analysis switch and the analysis page number). They must not be
        // sent to the API as filters — the backend paginates via `page` above
        // and doesn't understand ticket_analysis, which returns an empty set.
        if (
          key === "ticket_analysis" ||
          key === "page_no" ||
          value === null ||
          value === undefined ||
          value === "" ||
          (Array.isArray(value) && value.length === 0)
        ) {
          continue;
        }

        url += `&${key}=${value}`;
      }

      const { response, error } = await getRequest<
        TableDataWithPagination<Ticket>
      >(url);
      if (error) {
        ElMessageBox({
          title: "Failed",
          message:
            error?.response?.data?.message || error?.response?.data?.error,
          type: "error",
        });
        this.$state._tableData = [];
        this._loading = false;
        return;
      }

      const { data, ...pagination } = response.data;
      this.$state._tableData = data;
      this.$state._pagination = pagination;

      if (!this._filterData.referral_id) {
        const uniqueReferrals = new Map();

        data.forEach((ticket: any) => {
          const referral = ticket?.referral;

          if (!referral?.id || !referral?.title) return;

          if (!uniqueReferrals.has(referral.id)) {
            uniqueReferrals.set(referral.id, referral);
          }
        });

        this.$state._referrals = Array.from(uniqueReferrals.values());
      }

      this._loading = false;
    },

    async updateTicket(ticket: Ticket) {
      return await putRequest<Ticket>(`desk/tickets/${ticket.id}`, ticket);
    },

    async getStatuses() {
      const { response, error } = await getRequest<TicketStatus[]>(
        "desk/ticket-statuses",
      );
      if (!error) {
        this.$state._statuses = response.data;
      }
    },
    async getTicketMessagesForPreview(ticket: Ticket) {
      this.$state._ticketForPreview = ticket;
      const { response, error } = await getRequest<Conversation[]>(
        `desk/tickets/${ticket.id}/conversations`,
      );
      if (!error) {
        this.$state._ticketMessagesForPreview = response.data;
      }
    },
    async getSources() {
      const { response, error } = await getRequest<TicketSource[]>(
        "desk/tickets-sources",
      );
      if (!error) {
        this.$state._sources = response.data;
      }
    },

    async getPriorities() {
      const { response, error } = await getRequest<TicketPriority[]>(
        "desk/tickets-priorities",
      );
      if (!error) {
        this.$state._priorities = response.data;
      }
    },
    async exportTable(sortBy = "id", ascending = true) {
      const sortDir = ascending ? "asc" : "desc";
      let url = `desk/tickets/export?sort_column=${sortBy}&order=${sortDir}`;

      const { created_from: from, created_to: to } = this._filterData;
      if (!from || !to) {
        ElMessageBox({
          type: "error",
          title: "Request Error",
          message:
            "The ticket created period is required for exporting tickets.",
        });
        return;
      }

      for (const [key, value] of Object.entries(this._filterData)) {
        if (!value) continue;
        url += `&${key}=${value}`;
      }
      const formattedFrom = moment(from).format("YYYY-MM-DD");
      const formattedTo = moment(to).format("YYYY-MM-DD");
      const fileName = `tickets-${formattedFrom}-to-${formattedTo}`;

      await downloadWithSavePicker(url, fileName);
    },
    async unpick(ticketId: number, payload: Record<string, any> = {}) {
      return await putRequest<Ticket>(
        `desk/tickets/${ticketId}/unpick`,
        payload,
      );
    },

    async resolve(ticketId: number, payload: Record<string, any> = {}) {
      return await putRequest<Ticket>(
        `desk/tickets/${ticketId}/resolve`,
        payload,
      );
    },

    async pick(ticketId: number, assignToProfile = false) {
      return await putRequest<Ticket>(`desk/tickets/${ticketId}/pick`, {
        ...(assignToProfile
          ? {
              assign_to_profile: true,
              on_profile_conflict: DEFAULT_PROFILE_CONFLICT,
            }
          : {}),
      });
    },

    async bulkPick(
      includeAutomatedTickets = false,
      automationOutcome?: number | null,
      profileOptions: Record<string, any> = {},
    ) {
      const ticketIds = this.multipleSelection.map((t: Ticket) => t.id);

      const { response, error } = await putRequest<BulkPickResult>(
        "desk/tickets/bulk-pick",
        { ticket_ids: ticketIds, ...profileOptions },
      );
      if (error) {
        const errors = error.response?.data?.errors || {};
        const lines: string[] = Object.values(errors).flat() as string[];

        // The <ul> used to be opened unconditionally, so with no validation bag
        // this ended up as "<ul></ul>" — an empty but *truthy* string, which
        // short-circuited the `||` fallback and rendered a blank dialog. The
        // over-limit response carries a plain `message`, not an `errors` bag,
        // so that was every pick-limit error.
        const hasList = lines.length > 0;
        const message = hasList
          ? `<ul style="list-style-type: disc; padding-left: 20px;">${lines
              .map((line) => `<li>${line}</li>`)
              .join("")}</ul>`
          : error.response?.data?.message || error.message;

        ElMessageBox({
          title: "Ticket pick failed",
          message,
          // Only when we built the markup ourselves. A server sentence goes in
          // as plain text so it cannot inject anything.
          dangerouslyUseHTMLString: hasList,
          type: "info",
        });
        return error.response?.data;
      }
      await this.getTickets(
        1,
        "id",
        false,
        includeAutomatedTickets,
        automationOutcome,
      );
      reportBulkOutcome(response.data, response.data?.picked_ids ?? []);
      return response.data;
    },

    async bulkResolveTickets(
      ticketIds: number[],
      payload: Record<string, any> = {},
    ) {
      return await putRequest<BulkResolveResult>("desk/tickets/bulk-resolve", {
        ticket_ids: ticketIds,
        ...payload,
      });
    },

    async bulkResolve(
      includeAutomatedTickets = false,
      automationOutcome?: number | null,
    ) {
      const ticketIds = this.multipleSelection.map((t: Ticket) => t.id);
      const { response, error } = await this.bulkResolveTickets(ticketIds);
      if (!error) {
        await this.getTickets(
          1,
          "id",
          false,
          includeAutomatedTickets,
          automationOutcome,
        );
        reportBulkOutcome(response.data, response.data?.resolved_ids ?? []);
        return response.data;
      }
    },

    /**
     * The remark / category / tags edit, applied to every selected ticket.
     *
     * The outcome goes back to the caller rather than to a toast, the way the
     * inbox's bulk actions do: the modal that fired the run is where the agent is
     * still looking, and it can name what the server refused.
     *
     * Refetches the page the agent is on instead of jumping to page 1 — unlike
     * pick and resolve, an update usually leaves every ticket in the list, so
     * sending them back to the top would lose their place for nothing. Where it
     * does not, the refetch falls back to a page that still exists; see below. The
     * sort still resets to the list default, exactly as paging through the list
     * already does; only the page number is carried over.
     *
     * The refetch replaces the table rows, and el-table has no row-key here, so it
     * drops every tick. Whatever the server refused is therefore NOT still selected
     * — the modal names those ids rather than implying they are ready to retry.
     */
    async bulkUpdateTickets(
      payload: Record<string, any>,
      includeAutomatedTickets = false,
      automationOutcome?: number | null,
    ): Promise<BulkActionResponse<BulkUpdateResult>> {
      const ticketIds = this.multipleSelection.map((t: Ticket) => t.id);

      const { response, error } = await putRequest<BulkUpdateResult>(
        "desk/tickets/bulk-update",
        { ticket_ids: ticketIds, ...payload },
      );

      if (error) {
        const data = error.response?.data;
        // A 422 from the validator carries a bag keyed by field; the service's own
        // refusals carry a plain `message`. Show whichever one is there.
        const validationMessages = Object.values(
          data?.errors ?? {},
        ).flat() as string[];

        return {
          result: data ?? null,
          handledIds: [],
          errorMessage:
            validationMessages[0] ||
            data?.message ||
            error.message ||
            "Failed to update the selected tickets.",
        };
      }

      const page = this._pagination.current_page || 1;

      await this.getTickets(
        page,
        "id",
        false,
        includeAutomatedTickets,
        automationOutcome,
      );

      // Keeping the page is only safe while the edit cannot move tickets out of
      // the view. It can: the list filters on category and on tags, and this is
      // the action that writes them — change the category of every ticket on the
      // last page of a category filter and that page stops existing. Laravel
      // answers an out-of-range page with an empty `data` rather than an error,
      // so the agent would be left staring at a blank table with the paginator
      // still showing the page they were on, and no sign the update landed.
      const lastPage = this._pagination.last_page || 1;

      if (!this._tableData.length && page > lastPage) {
        await this.getTickets(
          lastPage,
          "id",
          false,
          includeAutomatedTickets,
          automationOutcome,
        );
      }

      return {
        result: response.data,
        handledIds: response.data?.updated_ids ?? [],
      };
    },

    async ticketBulkAssign(
      payload: {
        agent_ids: number[];
        ticket_ids: number[];
      },
      includeAutomatedTickets = false,
      automationOutcome?: number | null,
    ): Promise<BulkAssignResult> {
      const { response, error } = await putRequest<{
        message: string;
      }>("desk/tickets/bulk-assign", payload);

      if (!error) {
        ElMessage.success(response.data.message);

        await this.getTickets(
          1,
          "id",
          false,
          includeAutomatedTickets,
          automationOutcome,
        );

        return {
          success: true,
          message: response.data.message,
          agentErrors: [],
          errors: [],
        };
      }

      const data = error?.response?.data as {
        message?: string;
        error?: string;
        agent_errors?: BulkAssignAgentError[];
        errors?: Record<string, string[]>;
      };

      return {
        success: false,
        message: data?.message || data?.error || "Bulk assign failed.",
        agentErrors: data?.agent_errors ?? [],
        errors: Object.values(data?.errors ?? {}).flat(),
      };
    },

    async getTicketGroups() {
      const { response, error } = await getRequest<TicketGroup[]>(
        "desk/ticket-groups",
      );
      if (!error) {
        this.$state._ticketGroups = response.data;
      }
    },
    async addTicketGroup(group: TicketGroup) {
      const { response, error } = await postRequest<TicketGroup>(
        "desk/ticket-groups",
        group,
      );
      if (!error) {
        return null;
      }
      return error.response?.data;
    },
    async updateTicketGroup(group: TicketGroup) {
      const { response, error } = await putRequest<TicketGroup>(
        `desk/ticket-groups/${group.id}`,
        group,
      );
      if (!error) {
        return null;
      }
      return error.response?.data;
    },
    async deleteTicketGroup(group: TicketGroup) {
      return await deleteRequest(`desk/ticket-groups/${group.id}`);
    },
    isTicketForMe(ticket: Ticket): boolean {
      const configStore = useConfigStore();
      if (
        Array.isArray(configStore.globalConfig.ticket_groups) &&
        configStore.globalConfig.ticket_groups.length > 0
      ) {
        return configStore.globalConfig.ticket_groups.some(
          (ticketGroup) =>
            ticketGroup.channel_id === ticket.channel_id &&
            ticketGroup.source_type === ticket.source_type &&
            ticketGroup.source_platform === ticket.source_platform,
        );
      }
      return true;
    },

    doesTicketPassFilter(ticket: Ticket): boolean {
      const {
        priority,
        status,
        to,
        from,
        search,
        owner,
        phone,
        agent,
        text,
        source_platform,
        source_type,
        category,
        tags,
        channel_id,
        channel_type,
        channel_platform,
        referral_id,
        skipped,
      } = this._filterData;

      if (
        priority &&
        priority.length > 0 &&
        !priority.includes(ticket.priority)
      ) {
        return false;
      }

      if (status && status.length > 0 && !status.includes(ticket.status)) {
        return false;
      }

      const ticketDate = new Date(ticket.original_created_at).getTime();
      const fromDate = from ? new Date(from).getTime() : null;
      const toDate = to ? new Date(to).getTime() : null;

      if (
        (fromDate && ticketDate < fromDate) ||
        (toDate && ticketDate > toDate)
      ) {
        return false;
      }

      if (
        search &&
        !(
          ticket.id == search ||
          ticket.title?.toLowerCase().includes(search.toLowerCase()) ||
          ticket.remarks?.toLowerCase().includes(search.toLowerCase())
        )
      ) {
        return false;
      }

      if (
        owner &&
        !ticket.owner?.name?.toLowerCase().includes(owner.toLowerCase())
      ) {
        return false;
      }

      if (agent && ticket.agent_id !== agent) {
        return false;
      }

      if (source_platform && ticket.source_platform !== source_platform) {
        return false;
      }

      if (source_type && ticket.source_type !== source_type) {
        return false;
      }

      if (category && ticket.category_id !== category) {
        return false;
      }

      if (
        channel_id &&
        (ticket.channel_id !== channel_id ||
          ticket.channel_type !== channel_type ||
          ticket.channel_platform !== channel_platform)
      ) {
        return false;
      }
      if (referral_id && ticket.referral_id !== referral_id) {
        return false;
      }
      if (
        skipped !== null &&
        skipped !== undefined &&
        ticket.skipped !== skipped
      ) {
        return false;
      }
      if (category) {
        if (category === "____" && ticket.category_id) {
          return false;
        }

        if (category !== "____" && ticket.category_id !== category) {
          return false;
        }
      }
      if (tags && tags.length > 0) {
        if (tags.includes("____")) {
          if (ticket.tags?.length) {
            return false;
          }
        } else {
          const ticketTagIds = ticket.tags?.map((tag: any) => tag.id) ?? [];

          const hasMatchingTag = tags.some((tagId: number) =>
            ticketTagIds.includes(tagId),
          );

          if (!hasMatchingTag) {
            return false;
          }
        }
      }

      return true;
    },
    setShowModalForTicketMessagesPreviewVisibility(visibility: boolean) {
      this._showModalForTicketMessagesPreview = visibility;
    },

    async getScheduledMessages(
      pageNo = 1,
      paginate = false,
      filters: Record<string, any> = {},
    ) {
      this._scheduledMessagesLoading = true;
      let url = `desk/ticket-scheduled-messages?page=${pageNo}&paginate=${paginate}&per_page=${this._scheduledMessagesPerPage}&order_by=scheduled_at&dir=asc`;
      for (const [key, value] of Object.entries(filters)) {
        if (value === null || value === undefined || value === "") continue;
        url += `&${key}=${encodeURIComponent(value)}`;
      }

      const { response, error } = await getRequest<
        TableDataWithPagination<TicketScheduledMessage>
      >(url);
      if (error) {
        ElMessageBox({
          title: "Failed",
          message:
            error?.response?.data?.message || error?.response?.data?.error,
          type: "error",
        });
        this.$state._scheduledMessages = [];
        this._scheduledMessagesLoading = false;
        return;
      }

      // paginate=false returns a bare array instead of a paginator envelope
      if (Array.isArray(response.data)) {
        this.$state._scheduledMessages = response.data;
        this.$state._scheduledMessagesPagination = {} as Pagination;
      } else {
        const { data, ...pagination } = response.data;
        this.$state._scheduledMessages = data;
        this.$state._scheduledMessagesPagination = pagination;
      }
      this._scheduledMessagesLoading = false;
    },

    async getScheduledMessage(id: number) {
      const { response, error } = await getRequest<TicketScheduledMessage>(
        `desk/ticket-scheduled-messages/${id}`,
      );
      if (!error) {
        this.$state._selectedScheduledMessage = response.data;
      }
    },

    async createScheduledMessage(payload: {
      ticket_id: number;
      schedules: Array<{ message: string; scheduled_at: string }>;
    }) {
      return await postRequest<{ message: string }>(
        "desk/ticket-scheduled-messages",
        payload,
      );
    },

    async updateScheduledMessage(id: number, message: string) {
      return await putRequest<TicketScheduledMessage>(
        `desk/ticket-scheduled-messages/${id}`,
        { message },
      );
    },

    // The API has no destroy route; "deleting" cancels the pending schedule.
    // The backend records its own cancelled_reason ("<user> cancelled the
    // schedule.") — a client-sent reason is ignored.
    async deleteScheduledMessage(id: number) {
      return await putRequest<TicketScheduledMessage>(
        `desk/ticket-scheduled-messages/${id}`,
        { cancel: true },
      );
    },

    // Drop a "deleted" schedule from the loaded list without refetching —
    // the row still exists as Cancelled, so a refetch would bring it back.
    removeScheduledMessageFromList(id: number) {
      this.$state._scheduledMessages = this.$state._scheduledMessages.filter(
        (schedule: TicketScheduledMessage) => schedule.id !== id,
      );
    },

    setScheduleCreateModalVisibility(visibility: boolean) {
      this.showScheduleCreateModal = visibility;
      // The draft is only meaningful for the open it was set for.
      if (!visibility) this.scheduleDraftMessage = "";
    },
    setScheduleDraftMessage(message: string) {
      this.scheduleDraftMessage = message;
    },
    setScheduleEditModalVisibility(visibility: boolean) {
      this.showScheduleEditModal = visibility;
    },
    // The list page is paginated and always reads the stored filters; the
    // inbox side list calls getScheduledMessages directly with its own.
    async fetchScheduledMessagesPage(pageNo = 1) {
      await this.getScheduledMessages(
        pageNo,
        true,
        this.$state._scheduledMessageFilters,
      );
    },
    setScheduledMessagesPerPage(perPage: number) {
      this.$state._scheduledMessagesPerPage = perPage;
    },
    setScheduledMessageFilters(filters: Record<string, any>) {
      this.$state._scheduledMessageFilters = Object.fromEntries(
        Object.entries(filters).filter(
          ([, value]) => value !== null && value !== undefined && value !== "",
        ),
      );
    },
  },
  getters: {
    showEditOptions(state) {
      return state.multipleSelection.length > 0;
    },
    tableData: (state) => state._tableData,
    statuses: (state) => {
      return state._statuses;
    },
    ticketMessagesForPreview: (state) => state._ticketMessagesForPreview,
    ticketForPreview: (state) => state._ticketForPreview,
    sources: (state) => state._sources,
    showModalForTicketMessagesPreview: (state) =>
      state._showModalForTicketMessagesPreview,
    priorities: (state) => state._priorities,
    pagination: (state) => state._pagination,
    ticketGroups: (state) => state._ticketGroups,
    referrals: (state) => state._referrals,
    scheduledMessages: (state) => state._scheduledMessages,
    scheduledMessagesLoading: (state) => state._scheduledMessagesLoading,
    scheduledMessagesPagination: (state) => state._scheduledMessagesPagination,
    selectedScheduledMessage: (state) => state._selectedScheduledMessage,
    scheduledMessageFilters: (state) => state._scheduledMessageFilters,
    scheduledMessagesPerPage: (state) => state._scheduledMessagesPerPage,
  },
});
